import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBell,
  lucideCode,
  lucideCreditCard,
  lucideFileText,
  lucideGift,
  lucideHandshake,
  lucideLandmark,
  lucidePalette,
  lucidePercent,
  lucideStore,
  lucideTicket,
  lucideUser,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmSidebarImports } from '@spartan-ng/helm/sidebar';
import { AuthService } from '../../../services/auth.service';
import {
  SETTINGS_NAV,
  SettingsNavGroup,
  SettingsNavItem,
  isNavItemVisible,
  navLabel,
} from '../settings-nav';

/**
 * Settings navigation. Purely presentational: it filters the nav tree for the
 * current role and reports the selected param, the parent owns the routing.
 */
@Component({
  selector: 'app-settings-sidebar',
  imports: [NgIcon, HlmSidebarImports],
  providers: [
    provideIcons({
      lucideBell,
      lucideCode,
      lucideCreditCard,
      lucideFileText,
      lucideGift,
      lucideHandshake,
      lucideLandmark,
      lucidePalette,
      lucidePercent,
      lucideStore,
      lucideTicket,
      lucideUser,
      lucideUsers,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- The hlm-sidebar wrapper itself is skipped on purpose: with
         collapsible="none" it forces h-svh on its host, which would stretch the
         border across the whole viewport. The menu parts below are unchanged. -->
    <nav
      class="bg-sidebar text-sidebar-foreground border-border flex flex-col overflow-hidden rounded-xl border"
      aria-label="Einstellungen">
      <div hlmSidebarHeader class="px-4 py-3">
        <p class="text-base font-medium">Einstellungen</p>
        <p class="text-muted-foreground text-xs">Konfiguriere deine Gutschein-App</p>
      </div>

      <div hlmSidebarContent>
        @for (group of visibleGroups(); track group.label) {
          <div hlmSidebarGroup>
            <div hlmSidebarGroupLabel>{{ group.label }}</div>
            <div hlmSidebarGroupContent>
              <ul hlmSidebarMenu>
                @for (item of group.items; track item.param) {
                  <li hlmSidebarMenuItem>
                    <button
                      hlmSidebarMenuButton
                      [isActive]="isItemActive(item)"
                      (click)="selectItem(item)">
                      <ng-icon [name]="item.icon ?? ''" />
                      <span>{{ labelFor(item) }}</span>
                    </button>

                    @if (isItemActive(item) && hasSubMenu(item)) {
                      <ul hlmSidebarMenuSub>
                        @for (child of visibleChildren(item); track child.param) {
                          <li hlmSidebarMenuSubItem>
                            <button
                              hlmSidebarMenuSubButton
                              [isActive]="activeParam() === child.param"
                              (click)="select.emit(child.param); picked.emit()">
                              <span>{{ labelFor(child) }}</span>
                            </button>
                          </li>
                        }
                      </ul>
                    }
                  </li>
                }
              </ul>
            </div>
          </div>
        }
      </div>
    </nav>
  `,
})
export class SettingsSidebarComponent {
  /** Currently selected param, including sub items. */
  readonly activeParam = input<string | null>(null);
  readonly isMember = input(false);
  readonly isNetwork = input(false);

  readonly select = output<string>();
  /**
   * The choice is final: a leaf or a sub item. A parent that just unfolds its
   * sub menu doesn't fire this, so the mobile sheet can stay open for it.
   */
  readonly picked = output<void>();

  private readonly actingPartnerName = inject(AuthService).actingPartnerName;

  private readonly audience = computed(() => ({
    isMember: this.isMember(),
    isNetwork: this.isNetwork(),
    isActingPartner: !!this.actingPartnerName(),
  }));

  protected readonly visibleGroups = computed<SettingsNavGroup[]>(() =>
    SETTINGS_NAV.map((group) => ({
      ...group,
      items: group.items.filter((item) => isNavItemVisible(item, this.audience())),
    })).filter((group) => group.items.length > 0),
  );

  protected visibleChildren(item: SettingsNavItem): SettingsNavItem[] {
    if (!item.children) return [];
    return item.children.filter((child) => isNavItemVisible(child, this.audience()));
  }

  protected labelFor(item: SettingsNavItem): string {
    return navLabel(item, this.audience());
  }

  protected selectItem(item: SettingsNavItem): void {
    this.select.emit(this.defaultParamFor(item));
    if (!this.hasSubMenu(item)) this.picked.emit();
  }

  /** A single visible sub item is the entry itself: no sub list. */
  protected hasSubMenu(item: SettingsNavItem): boolean {
    return this.visibleChildren(item).length > 1;
  }

  /** A parent with children opens its first visible child. */
  protected defaultParamFor(item: SettingsNavItem): string {
    return item.children ? (this.visibleChildren(item)[0]?.param ?? item.param) : item.param;
  }

  protected isItemActive(item: SettingsNavItem): boolean {
    const active = this.activeParam();
    if (!active) return false;
    if (item.param === active) return true;
    return !!item.children?.some((child) => child.param === active);
  }
}
